import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Sparkles, Sprout, MoveDown, ArrowRight } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { CATEGORIES, featuredProducts } from "../data/products";

const HeroLine = ({ children, delay = 0 }) => (
  <span className="hero-mask">
    <motion.span className="hero-line" initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.22, 0.61, 0.36, 1] }}>{children}</motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, y = 40, className }) => (
  <motion.div className={className} initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 0.61, 0.36, 1] }}>{children}</motion.div>
);

export default function Home() {
  const heroRef = useRef(null);
  const featured = featuredProducts();

  return (
    <>
      <h1 className="sr-only">Bhoomiza Organics — Natural Foods, Dehydrated Fruits &amp; Vegetables, Moringa, Natural Powders and Moringa Sweets</h1>

      <section className="hero-section hero-section-text" ref={heroRef}>
        <div className="hero-copy hero-copy-centered">
          <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.6 }}>
            <span /> Bhoomiza Organics · Pure · Natural · Carefully Dehydrated
          </motion.p>
          <h2 className="hero-headline">
            <HeroLine delay={0.7}>Natural foods,</HeroLine>
            <HeroLine delay={0.85}><em>rooted in nature.</em></HeroLine>
          </h2>
          <motion.p className="hero-intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.7 }}>
            <strong>Bhoomiza Organics</strong> — Moringa · Dehydrated Fruits · Dehydrated Vegetables · Natural Powders · Moringa Sweets. Made with carefully selected ingredients from a Jaipur-based brand, delivered across India.
          </motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8, duration: 0.7 }}>
            <Link to="/shop" className="primary-link" data-testid="hero-shop-button">Browse the range <MoveDown size={16} /></Link>
            <span className="hero-note">Prices &amp; delivery confirmed on WhatsApp</span>
          </motion.div>
        </div>
      </section>

      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i}>प्रकृति से · Farm sourced · अच्छा खाएं · Naturally dehydrated · अच्छा जिएं · Small batch · जीवन का हरा · Nature-first &nbsp;✳&nbsp;</span>
          ))}
        </div>
      </div>

      {/* Categories */}
      <section className="cat-section" id="categories">
        <Reveal>
          <p className="chapter-tag"><em>Chapter</em> 01</p>
          <p className="eyebrow"><span /> Shop by Category</p>
          <h2>Explore our <em>natural range.</em></h2>
        </Reveal>
        <div className="cat-grid cat-grid-large">
          {CATEGORIES.map((c, i) => {
            const emoji = c.id === "moringa-powder" ? "🌿" : c.id === "fruit-chips" ? "🍎" :
                          c.id === "vegetable-chips" ? "🥕" : c.id === "fruit-vegetable-powders" ? "🌱" : "🍬";
            const desc = c.id === "moringa-powder" ? "Premium moringa leaf powder for everyday routines." :
                         c.id === "fruit-chips" ? "Naturally dehydrated fruits — a light, crisp option." :
                         c.id === "vegetable-chips" ? "Dehydrated vegetables for daily snacking and cooking." :
                         c.id === "fruit-vegetable-powders" ? "Finely milled fruit and vegetable powders for kitchens." :
                         "Traditional-style sweets crafted with Moringa.";
            return (
              <Reveal key={c.id} delay={i * 0.06}>
                <div className="cat-card cat-card-lg" style={{ "--tint": c.tint }} data-testid={`cat-${c.id}`}>
                  <span className="cat-emoji" aria-hidden="true">{emoji}</span>
                  <div className="cat-card-body">
                    <span className="cat-name">{c.label}</span>
                    <p className="cat-desc">{desc}</p>
                    <Link to={`/shop/${c.id}`} className="cat-shop-now" data-testid={`cat-cta-${c.id}`}>
                      Shop Now <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Featured products */}
      <section className="featured-section" id="featured">
        <Reveal>
          <p className="chapter-tag"><em>Chapter</em> 02</p>
          <p className="eyebrow"><span /> Featured products</p>
          <h2>Made naturally, <em>packed with care.</em></h2>
        </Reveal>
        <div className="product-grid">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
        <div className="featured-cta">
          <Link to="/shop" className="primary-link" data-testid="shop-all-button">Shop All Products <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section" id="benefits">
        <div className="section-heading">
          <Reveal>
            <p className="chapter-tag light"><em>Chapter</em> 03</p>
            <p className="eyebrow"><span /> Why Bhoomiza?</p>
            <h2>Naturally made, <em>simply delivered.</em></h2>
          </Reveal>
        </div>
        <div className="benefit-grid">
          {[
            { icon: <Sparkles />, title: "Made with care", body: "Carefully selected ingredients, no unnecessary additives. Just clean, natural products.", num: "i" },
            { icon: <Leaf />, title: "Naturally dehydrated", body: "Gently dehydrated fruits, vegetables and leaves — for everyday cooking and snacking.", num: "ii" },
            { icon: <Sprout />, title: "From Jaipur, for India", body: "A Jaipur-based brand delivering natural products across India.", num: "iii" },
          ].map((b, i) => (
            <motion.div key={b.title} data-testid={`benefit-${i}`}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 0.61, 0.36, 1] }}
              whileHover={{ y: -6 }}>
              <span className="benefit-num">{b.num}</span>
              {b.icon}<h3>{b.title}</h3><p>{b.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Usage */}
      <section className="usage-section" id="usage">
        <Reveal>
          <p className="chapter-tag"><em>Chapter</em> 04</p>
          <p className="eyebrow"><span /> Make it yours</p>
          <h2>Simple ways to add <em>Bhoomiza</em> to your day.</h2>
        </Reveal>
        <Reveal delay={0.1} className="usage-copy">
          <p>Stir a spoon of natural powder into your morning drink, snack on a handful of dehydrated fruit, or use dehydrated vegetables and powders in everyday cooking. Small, easy additions — that's the Bhoomiza way.</p>
          <span>Simple enough for every day. Powerful enough to keep.</span>
        </Reveal>
      </section>
    </>
  );
}
